/**
 * LEGACY_DECOMMISSIONED (May 22, 2026)
 * Part of the legacy upload subsystem. Frozen. Do not extend.
 * Replacement: separate Video Analysis Engine (see VAE_CLEAN_ROOM_ARCHITECTURE.md).
 *
 * uploadPersistence — Background-safe upload metadata store (Feb 7 2026 P0).
 *
 * The browser cannot preserve a `File` object across a page unload
 * (mobile Safari / iOS will kill the tab when the user backgrounds
 * the app for long enough). What we CAN preserve is the upload_id
 * plus enough file-identity metadata (name + size + lastModified +
 * duration + type) to re-verify the file once the user returns.
 *
 * Iteration Feb 8, 2026 — iPhone-recovery hardening:
 *   • Persist `videoDurationSeconds` (probed at pick time).
 *   • Persist `fileType` (video/mp4 etc) — extra mismatch signal.
 *   • Persist `lastProgressPct` for the recovery banner copy.
 *   • Mismatch checker now returns a STRUCTURED diff so the UI can
 *     tell the user exactly which field doesn't match (name vs
 *     size vs duration vs lastModified) — not a bare boolean.
 *
 * Flow:
 *   1. As soon as we know the upload_id (first onProgress tick from
 *      chunkedUpload, or on pause), persist a record here.
 *   2. Also persist on `visibilitychange` (page going hidden) so iOS
 *      Safari killing the tab doesn't lose the pointer.
 *   3. On page mount, call `loadPendingUpload(userId, purpose)` —
 *      if a record exists + the server's /status says resumable,
 *      prompt the user to re-pick the same file to continue.
 *   4. After verifying the picked file matches (name + size +
 *      lastModified + duration), call chunkedUpload with
 *      `resumeUploadId`.
 *   5. Clear the record on successful complete OR explicit cancel.
 *
 * Records older than TTL are considered dead (the backend session
 * itself only lives 24 h anyway).
 */

const LS_KEY_PREFIX = "ndg_pending_upload_v1";
const RECORD_TTL_MS = 24 * 60 * 60 * 1000; // 24 h matches backend TTL

function _key(userId, purpose) {
  // Scope the record to the current user + upload purpose so two
  // different uploaders can coexist (lesson review vs clip basket).
  return `${LS_KEY_PREFIX}::${userId || "anon"}::${purpose || "lesson_review"}`;
}

function _lsAvailable() {
  try {
    return typeof window !== "undefined" && !!window.localStorage;
  } catch {
    return false;
  }
}

/**
 * Save / refresh a pending-upload record. Idempotent — call this on
 * every meaningful state change (new upload started, pause, progress
 * milestone, page going hidden) so the record is always fresh.
 *
 *   savePendingUpload({
 *     userId, purpose,                 // scoping
 *     uploadId,
 *     fileName, fileSize, fileLastModified,
 *     fileType,                        // "video/mp4" etc
 *     videoDurationSeconds,            // best-effort, may be null
 *     totalChunks,
 *     lastSuccessfulChunk,             // -1 if none yet
 *     lastProgressPct,                 // 0-100 (for the recovery card)
 *     reason,                          // "uploading" | "paused-network" | "paused-auth"
 *   });
 */
export function savePendingUpload(record) {
  if (!_lsAvailable()) return false;
  if (!record?.userId || !record?.uploadId) return false;
  // Read any existing record so we never DROP a value the caller
  // didn't pass on this tick — duration is probed once at pick time
  // but every subsequent progress tick re-saves. Without merge, the
  // duration would silently disappear on the second save.
  const prior = loadPendingUpload(record.userId, record.purpose) || {};
  const toStore = {
    v: 2,                              // bumped — new fields below
    savedAt: Date.now(),
    startedAt: record.startedAt || prior.startedAt || Date.now(),
    userId: record.userId,
    purpose: record.purpose || "lesson_review",
    uploadId: record.uploadId,
    fileName: record.fileName ?? prior.fileName ?? null,
    fileSize: Number(record.fileSize ?? prior.fileSize ?? 0),
    fileLastModified: Number(
      record.fileLastModified ?? prior.fileLastModified ?? 0,
    ),
    fileType: record.fileType ?? prior.fileType ?? null,
    videoDurationSeconds: (
      typeof record.videoDurationSeconds === "number"
        ? record.videoDurationSeconds
        : (typeof prior.videoDurationSeconds === "number"
            ? prior.videoDurationSeconds
            : null)
    ),
    totalChunks: Number(record.totalChunks ?? prior.totalChunks ?? 0),
    lastSuccessfulChunk: Number(
      Number.isFinite(record.lastSuccessfulChunk)
        ? record.lastSuccessfulChunk
        : (Number.isFinite(prior.lastSuccessfulChunk)
            ? prior.lastSuccessfulChunk
            : -1),
    ),
    lastProgressPct: (
      typeof record.lastProgressPct === "number"
        ? record.lastProgressPct
        : (typeof prior.lastProgressPct === "number"
            ? prior.lastProgressPct
            : 0)
    ),
    reason: record.reason || prior.reason || "uploading",
  };
  try {
    window.localStorage.setItem(
      _key(record.userId, record.purpose),
      JSON.stringify(toStore),
    );
    return true;
  } catch {
    // Quota exceeded / storage blocked — silent no-op.
    return false;
  }
}

/**
 * Load the pending-upload record for this user+purpose, if any.
 * Returns null when missing, expired, or malformed. Cleans up
 * expired records on read. v1 records are read forward into v2
 * shape (missing fields just resolve to null).
 */
export function loadPendingUpload(userId, purpose = "lesson_review") {
  if (!_lsAvailable()) return null;
  const key = _key(userId, purpose);
  let raw;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  let rec;
  try {
    rec = JSON.parse(raw);
  } catch {
    try { window.localStorage.removeItem(key); } catch { /* noop */ }
    return null;
  }
  // Accept v1 (no duration / type / lastProgressPct) or v2 — both
  // are safe to read; the recovery UI handles missing optional fields.
  if (!rec || (rec.v !== 1 && rec.v !== 2) || !rec.uploadId) {
    try { window.localStorage.removeItem(key); } catch { /* noop */ }
    return null;
  }
  const age = Date.now() - Number(rec.savedAt || 0);
  if (age < 0 || age > RECORD_TTL_MS) {
    try { window.localStorage.removeItem(key); } catch { /* noop */ }
    return null;
  }
  return rec;
}

/** Clear the pending-upload record (call on success OR user cancel). */
export function clearPendingUpload(userId, purpose = "lesson_review") {
  if (!_lsAvailable()) return;
  try {
    window.localStorage.removeItem(_key(userId, purpose));
  } catch { /* ignore */ }
}

/**
 * Probe a video's duration in seconds, best-effort. Returns null if
 * the browser can't read the metadata (e.g. unsupported codec or
 * the file is HEIC). 4-second cap so a slow probe never blocks the
 * upload start.
 *
 * Used at pick time so the recovery banner can later say
 * "Looking for the 19:03 video you picked today at 14:32".
 */
export async function probeVideoDurationSeconds(file) {
  if (!file || typeof window === "undefined" || typeof window.URL === "undefined") {
    return null;
  }
  return new Promise((resolve) => {
    let resolved = false;
    const finish = (val) => {
      if (resolved) return;
      resolved = true;
      try { URL.revokeObjectURL(url); } catch { /* noop */ }
      resolve(val);
    };
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    video.onloadedmetadata = () => {
      const d = Number(video.duration);
      finish(Number.isFinite(d) && d > 0 ? d : null);
    };
    video.onerror = () => finish(null);
    video.src = url;
    setTimeout(() => finish(null), 4000);   // hard cap
  });
}

/**
 * True/false convenience — kept for backwards compatibility. Use
 * `compareFileToPendingRecord` if you want the structured diff.
 */
export function fileMatchesPendingRecord(file, record) {
  return compareFileToPendingRecord(file, record).matches;
}

/**
 * Structured comparison — what differs (if anything) between the
 * file the user just picked and the persisted record. Drives the
 * "this doesn't look like the same video — please pick the original"
 * UX so the user knows EXACTLY why their pick was rejected.
 *
 * Returns:
 *   {
 *     matches: bool,
 *     mismatches: [
 *       { field: "name", expected: "...", got: "..." },
 *       { field: "size_bytes", expected: 12345, got: 67890 },
 *       { field: "duration_seconds", expected: 1142, got: 88 },
 *       { field: "last_modified", expected: 17..., got: 17... },
 *     ]
 *   }
 *
 * Optional fields (duration / lastModified / type) only register a
 * mismatch when BOTH sides are populated and differ — never on
 * "missing on one side".
 */
export function compareFileToPendingRecord(file, record) {
  if (!file || !record) return { matches: false, mismatches: [{ field: "file", expected: "any", got: null }] };
  const mismatches = [];
  if ((file.name || "") !== (record.fileName || "")) {
    mismatches.push({
      field: "name",
      expected: record.fileName || "",
      got: file.name || "",
    });
  }
  if (Number(file.size) !== Number(record.fileSize || 0)) {
    mismatches.push({
      field: "size_bytes",
      expected: Number(record.fileSize || 0),
      got: Number(file.size),
    });
  }
  if (
    record.fileLastModified
    && file.lastModified
    && Number(file.lastModified) !== Number(record.fileLastModified)
  ) {
    mismatches.push({
      field: "last_modified",
      expected: Number(record.fileLastModified),
      got: Number(file.lastModified),
    });
  }
  return { matches: mismatches.length === 0, mismatches };
}

/**
 * Async variant of `compareFileToPendingRecord` that ALSO probes
 * the picked video's duration and compares it (within ±1 s
 * tolerance) to the persisted duration. Use this when the record
 * has `videoDurationSeconds` populated — duration is the strongest
 * mismatch signal short of cryptographic hashing because it's much
 * harder to fake by accidentally picking a different file with the
 * same name (e.g. iCloud variants of the same recording).
 */
export async function compareFileToPendingRecordWithDuration(file, record) {
  const base = compareFileToPendingRecord(file, record);
  if (
    typeof record?.videoDurationSeconds === "number"
    && record.videoDurationSeconds > 0
  ) {
    const picked = await probeVideoDurationSeconds(file);
    if (typeof picked === "number" && picked > 0) {
      const expected = Number(record.videoDurationSeconds);
      // 1-second tolerance — different demuxers occasionally round
      // a 16:23.4 clip down to 16:23 vs up to 16:24.
      if (Math.abs(picked - expected) > 1.0) {
        base.mismatches.push({
          field: "duration_seconds",
          expected,
          got: picked,
        });
      }
    }
  }
  return { matches: base.mismatches.length === 0, mismatches: base.mismatches };
}

export const PENDING_UPLOAD_TTL_MS = RECORD_TTL_MS;


// ─── describeMismatch (E3 — May 14, 2026) ──────────────────────────
// Moved here from the now-deleted UploadRecoveryBanner component so
// LessonReview can still produce a human-readable mismatch reason for
// logging. Pure utility — no React dependency.

function _fmtBytes(n) {
  if (typeof n !== "number" || n <= 0) return "?";
  const KB = 1024, MB = KB * 1024, GB = MB * 1024;
  if (n >= GB) return `${(n / GB).toFixed(1)} GB`;
  if (n >= MB) return `${(n / MB).toFixed(1)} MB`;
  if (n >= KB) return `${Math.round(n / KB)} KB`;
  return `${n} B`;
}

function _fmtDuration(seconds) {
  if (typeof seconds !== "number" || seconds <= 0) return null;
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function describeMismatch(mismatches, record) {
  if (!Array.isArray(mismatches) || mismatches.length === 0) return null;
  // Prefer the most-meaningful field. Duration > size > name > lastModified.
  const order = ["duration_seconds", "size_bytes", "name", "last_modified", "file"];
  const first = mismatches
    .slice()
    .sort((a, b) => order.indexOf(a.field) - order.indexOf(b.field))[0];
  let detail = null;
  switch (first.field) {
    case "duration_seconds":
      detail = `duration is ${_fmtDuration(first.got) || `${first.got}s`} but we saved ${_fmtDuration(first.expected) || `${first.expected}s`}`;
      break;
    case "size_bytes":
      detail = `file is ${_fmtBytes(first.got)} but we saved ${_fmtBytes(first.expected)}`;
      break;
    case "name":
      detail = `picked "${first.got}" but we saved "${first.expected}"`;
      break;
    case "last_modified":
      detail = "the file's modified date doesn't match the original";
      break;
    default:
      detail = "no file selected";
  }
  return `Looks like a different video — ${detail}. Pick the original recording to continue, or tap "Start fresh".`;
}
